// @ts-nocheck

import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  BoxV2,
  ButtonUI,
  CustomThemeProvider,
  Input,
  MultipleTagSelector,
  Selector,
  showNotification,
  usePortal,
  useQueryFetch
} from 'sonata-react-components';

import { getDefaultMessageSelectorKpis, getMetricsKeys } from '@/helpers/reporting.helpers';
import { getCountriesWithEvents, getImpressionsKpiChartSelector } from '@/store/selectors/reporting.selectors';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getCartographyDictionary } from '@/ts-resources/reporting.resources';
import { connectReportingDataWithSonataLI } from '../../reporting.resources';
import { ReportingConnectSonataLIModalStyle } from './reportingConnectSonataLIModal.style';

function ReportingConnectSonataLIModal({ buttonProps }) {
  const { formatMessage } = useIntl();
  const modalRef = useRef();

  const campaignIds = useSelector(getCampaigns);
  const countries = useSelector(getCountriesWithEvents) || [];
  const _impressionsKpiChartOptions = useSelector(getImpressionsKpiChartSelector);
  const { data: countriesData = [] } = useQueryFetch(`dictionary_country_info_${countries.join('')}`, () =>
    getCartographyDictionary(countries)
  );

  const countryOptions = countriesData
    .map(countryData => ({
      id: countryData.id,
      label: countryData.name
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const getDefaultName = () =>
    formatMessage(
      {
        defaultMessage: 'Report',
        id: 'reporting.connectSonataLIModal.reportNameTemplate'
      },
      { date: moment(new Date()).format('DD MMM, YYYY - HH:mm') }
    );

  const [{ country, metrics, name }, setState] = useState({
    country: countries[0],
    metrics: [],
    name: getDefaultName()
  });

  const selectedCountryOption = countryOptions.find(option => option.id === country);

  const resetState = () => {
    setState({
      country: countries[0],
      metrics: [],
      name: getDefaultName()
    });
  };

  const onChangeName = newName => {
    setState(prevState => ({
      ...prevState,
      name: newName
    }));
  };

  const onChangeSelectedMetrics = items => {
    setState(prevState => ({
      ...prevState,
      metrics: items
    }));
  };

  const getMetricOptions = selectorOptions => {
    return selectorOptions.map(option => {
      return {
        label: formatMessage({
          defaultMessage: getDefaultMessageSelectorKpis(option),
          id: `reporting.metrics.${option}`
        }),
        value: getMetricsKeys(option)
      };
    });
  };

  const selectableOptions = useMemo(() => {
    return getMetricOptions([..._impressionsKpiChartOptions.metricOptions, ..._impressionsKpiChartOptions.kpisOptions]);
  }, [_impressionsKpiChartOptions]);

  const modalButtonDisabled = !metrics.length || !name || !country;

  const targetBody = usePortal('connectSonataLIModalContainer');

  const showConnectSonataLIModal = useCallback(() => {
    resetState();
    modalRef && modalRef.current && modalRef.current.openModal();
  }, [modalRef]);

  const handleOnChangeCountry = option => {
    setState(prevState => ({
      ...prevState,
      country: option.id
    }));
  };

  const onSubmit = async () => {
    try {
      await connectReportingDataWithSonataLI(name, metrics, campaignIds, country);
      modalRef && modalRef.current && modalRef.current.closeModal();
      resetState();
      showNotification(
        'info',
        formatMessage({
          defaultMessage: 'A new resource has been created in SONATA LI from this reporting',
          id: 'reporting.connectSonataLIModal.successResult'
        })
      );
    } catch (err) {
      console.error(err);
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'An error occurred while connecting to the Sonata LI',
          id: 'reporting.connectSonataLIModal.errorResult'
        })
      );
    }
  };

  return (
    <>
      <ButtonUI {...buttonProps} onClick={showConnectSonataLIModal} />
      {ReactDOM.createPortal(
        <CustomThemeProvider themeContrast="light">
          <ReportingConnectSonataLIModalStyle
            centerButton={
              <CustomThemeProvider themeContrast="dark">
                <ButtonUI
                  disabled={modalButtonDisabled}
                  onClick={onSubmit}
                  text={formatMessage({
                    defaultMessage: 'Create new source on LI',
                    id: 'reporting.connectSonataLIModal.button'
                  })}
                  type="primary"
                />
              </CustomThemeProvider>
            }
            className="connectSonataLIModal"
            closeModalCallback={resetState}
            description={formatMessage({
              defaultMessage: 'Save your data report as custom source in Sonata LI',
              id: 'reporting.connectSonataLIModal.description'
            })}
            ref={modalRef}
            title={formatMessage({
              defaultMessage: 'Connect report with Sonata LI',
              id: 'reporting.connectSonataLIModal.title'
            })}
            width="800px"
          >
            <div className="connectSonataLIModal__container">
              <div className="connectSonataLIModal__nameContainer">
                <Input
                  id="connectSonataLIModal__campaignSourceName__input"
                  label={formatMessage({
                    defaultMessage: 'Source name',
                    id: 'reporting.connectSonataLIModal.nameInput'
                  })}
                  name="connectSonataLIModalNameInput"
                  notFloatingPlaceholder
                  onChange={onChangeName}
                  shape="squareUI"
                  type="text"
                  value={name}
                />
                {countries.length > 1 && (
                  <Selector
                    defaultValue={selectedCountryOption ? [selectedCountryOption] : undefined}
                    label={formatMessage({
                      defaultMessage: 'Country',
                      id: 'insights.boundaryShape.country'
                    })}
                    onChange={handleOnChangeCountry}
                    options={countryOptions}
                    type="simple"
                    value={selectedCountryOption ? [selectedCountryOption] : undefined}
                  />
                )}
              </div>
              <div className="connectSonataLIModal__metricsSelectorContainer">
                <div className="connectSonataLIModal__metricsSelectorContainer__sectionHeader">
                  {formatMessage({
                    defaultMessage: 'Select the metrics you want to export',
                    id: 'reporting.connectSonataLIModal.metricsHeader'
                  })}
                </div>
                <BoxV2 borderColor="cardItemBorder" id="connectSonataLIModal-metricsSelectorContainer-box">
                  <BoxV2.Body className="connectSonataLIModal__metricsSelectorContainer__Box">
                    <MultipleTagSelector
                      items={selectableOptions}
                      onChange={onChangeSelectedMetrics}
                      selectedBgColor="#20BEA8"
                    />
                  </BoxV2.Body>
                </BoxV2>
              </div>
            </div>
          </ReportingConnectSonataLIModalStyle>
        </CustomThemeProvider>,
        targetBody
      )}
    </>
  );
}

ReportingConnectSonataLIModal.propTypes = {
  buttonProps: PropTypes.object.isRequired
};

export default React.memo(ReportingConnectSonataLIModal);
